/**
 * Tipos del Diagrama Espacio-Tiempo (Time-Location Chart).
 */

/** Capas de contexto que se cargan como imagen por proyecto. */
export type ProjectChartImageType = 'SATELLITE_MAP' | 'ELEVATION_PROFILE';

/** Torre posicionada sobre el eje espacial. */
export interface TowerChainage {
    towerId: number;
    towerNumber: string;
    /** Distancia acumulada desde el inicio de la línea, en kilómetros. */
    chainageKm: number;
    /** Coordenadas importadas desde el KMZ. Nulas si el proyecto no lo tiene cargado. */
    latitude: number | null;
    longitude: number | null;
}

/** Punto del diagrama: progresiva en X, fecha de término en Y. */
export interface ChartPoint {
    towerNumber: string;
    chainageKm: number;
    /** Fecha en formato ISO (yyyy-MM-dd). */
    date: string;
}

/** Qué fecha representa una serie. */
export type DateMode = 'START' | 'COMPLETION';

/** Serie de una actividad. */
export interface ActivitySeries {
    activityId: number;
    activityName: string;
    subActivity: string | null;
    section: string | null;
    cip: string | null;
    /** START si grafica la fecha de inicio, COMPLETION si grafica la de término. */
    dateMode: DateMode;
    points: ChartPoint[];
}

/**
 * Cruce entre dos series.
 * Indica que en ese tramo el orden de las fechas se invierte, lo que es imposible en obra.
 */
export interface Crossing {
    activityAId: number;
    activityAName: string;
    activityBId: number;
    activityBName: string;
    chainageKm: number;
    date: string;
    fromTowerNumber: string;
    toTowerNumber: string;
}

/** Respuesta completa del diagrama. */
export interface TimeLocationChartData {
    towers: TowerChainage[];
    series: ActivitySeries[];
    crossings: Crossing[];
    /** Torres sin vano adelante, que quedaron fuera del eje espacial. */
    excludedTowers: string[];
    /** Longitud que define la escala del eje espacial. */
    totalLengthKm: number;
    /** GEODESIC si hay coordenadas del KMZ, FORWARD_SPAN si se usó la suma de vanos. */
    chainageSource: 'GEODESIC' | 'FORWARD_SPAN';
    /** Longitud según la suma de vanos del mantenedor, siempre informada para comparar. */
    totalSpanLengthKm: number;
}

/** Cuántas torres del proyecto tienen coordenada cargada. */
export interface TowerCoordinateStatus {
    totalTowers: number;
    towersWithCoordinates: number;
}

/** Torre emparejada con un punto del KMZ. */
export interface KmzMatchedTower {
    towerNumber: string;
    kmzName: string;
    latitude: number;
    longitude: number;
}

/** Resultado de leer un KMZ contra las torres del proyecto. */
export interface KmzImportResult {
    pointsInFile: number;
    towersInProject: number;
    matched: KmzMatchedTower[];
    towersWithoutPoint: string[];
    pointsWithoutTower: string[];
    applied: boolean;
}

/** Etiqueta legible de una serie, con la subactividad cuando existe. */
export const seriesLabel = (series: ActivitySeries): string =>
    series.subActivity && series.subActivity.trim() !== ''
        ? `${series.activityName} - ${series.subActivity}`
        : series.activityName;

/**
 * Etiqueta para la leyenda, con el modo de fecha entre paréntesis.
 * Sin esto el gráfico es ambiguo: dos series iguales podrían estar midiendo cosas distintas.
 */
export const seriesLegendLabel = (series: ActivitySeries): string =>
    `${seriesLabel(series)} (${series.dateMode === 'START' ? 'inicio' : 'término'})`;
